import { blog, website } from '@getpopsure/private-constants';
import ImageCard from 'components/imageCard';
import {
  ENGLISH_LOCALE_IDENTIFIER,
  FORMATTED_DEFAULT_LOCALE,
  GERMAN_LOCALE_IDENTIFIER,
} from 'constants/i18n';
import { nonProductLinks } from 'constants/internalLinks';
import { DEFAULT_SUBDOMAIN } from 'constants/subdomains';
import images from 'content/about-us/images';
import news from 'content/about-us/news';
import { GetStaticProps } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import React from 'react';
import { getSubdomain } from 'util/getSubdomain';
import { i18nLocaleFormatter } from 'util/i18nLocaleFormatter';

import BannerCard from './components/BannerCard';
import {
  Trail,
  TrailFirstEnd,
  TrailFirstStart,
  TrailLast,
} from './components/Trails';
import styles from './style.module.scss';

const AboutUs = (): JSX.Element => {
  const { t } = useTranslation();
  const router = useRouter();
  const imgLocaleKey =
    router.locale === GERMAN_LOCALE_IDENTIFIER
      ? GERMAN_LOCALE_IDENTIFIER
      : ENGLISH_LOCALE_IDENTIFIER;
  const subdomain = getSubdomain();

  return (
    <>
      <NextSeo
        title="About us"
        description="We founded Feather to keep insurance simple and accessible. Read more about our mission and team here."
      />
      <div className={styles.content}>
        <section className={styles['section-top']}>
          <div className="p-body">
            <header className={styles.header}>
              <h1
                data-aos="fade-up"
                data-aos-once="true"
                data-aos-duration="800"
                className={`p-h1 p--serif tc-primary-25 ${styles['header-h1']}`}
              >
                {t('aboutus:section.1.title.1')}
                <span className={`d-block ${styles.nowrap}`}>
                  {t('aboutus:section.1.title.2')}
                  <span className={`fs-italic ${styles.fontWeight900}`}>
                    {t('aboutus:section.1.title.3')}
                  </span>
                </span>
              </h1>
              <div className={styles['indicator-how-we-do-it']}>
                <p className="p-h3 mt24 mb16 p--serif tc-primary-25">
                  {t('aboutus:section.1.description')}
                </p>
                <TrailFirstStart />
              </div>
            </header>
          </div>
        </section>
      </div>
      <section className={styles['section-how']}>
        <div className="p-body">
          <TrailFirstEnd />
          <div className="mt24 mb24">
            <BannerCard
              title={t('aboutus:section.how.transparentadvice.title')}
              description={t(
                'aboutus:section.how.transparentadvice.description'
              )}
              image={images.transparentadvice[imgLocaleKey]}
              imageAlt={t('aboutus:section.how.transparentadvice.image.alt')}
            />
          </div>
          <Trail />
          <div className="mt24 mb24">
            {/* Not using BannerCard component because image requires special handling */}
            <div
              className={`bg-white br16 d-flex fd-column ai-center w100 gap32 ${styles['banner-card']}`}
            >
              <div
                className={`w100 d-flex jc-center ${styles['banner-card-image']}`}
              >
                <Image
                  src={images.lifestyle[imgLocaleKey]}
                  alt={t('aboutus:section.how.lifestyle.image.alt')}
                  style={{
                    objectFit: 'contain',
                    height: 'auto',
                    width: '100%',
                  }}
                  sizes="(max-width: 720px) 100vw, 40vw"
                />
              </div>
              <div className={styles['banner-card-text']}>
                <div className="p-h3 p--serif">
                  {t('aboutus:section.how.lifestyle.title')}
                </div>
                <div
                  data-aos="animate-underline"
                  data-aos-once="true"
                  className={`br8 bg-purple-500 ${styles.separator}`}
                />
                <p className="p-p mt16 tc-grey-600">
                  {t('aboutus:section.how.lifestyle.description')}
                </p>
              </div>
            </div>
          </div>
          <Trail reverse />
          <div className="mt24 mb24">
            <BannerCard
              title={t('aboutus:section.how.oneaccount.title')}
              description={t('aboutus:section.how.oneaccount.description')}
              image={images.oneaccount[imgLocaleKey]}
              imageAlt={t('aboutus:section.how.oneaccount.image.alt')}
            />
          </div>
          <TrailLast />
          <h3 className="p-h3 pt24 pb96 p--serif ta-center tc-grey-700">
            <div>{t('aboutus:section.how.punchline.1')}</div>
            {t('aboutus:section.how.punchline.2')}
          </h3>
        </div>
      </section>
      <section className={styles['section-history']}>
        <div className={`p-body ${styles['section-history-content']}`}>
          <div className={styles['section-history-text']}>
            <div className={`p-h2 p--serif ${styles['section-history-h2']}`}>
              {t('aboutus:section.history.title')}
            </div>
            <div
              data-aos="animate-underline"
              data-aos-once="true"
              className={styles.separator}
            />
            <p className="p-p mt16">
              {t('aboutus:section.history.description.1')}
            </p>
            <p className="p-p mt16">
              {t('aboutus:section.history.description.2')}
            </p>
            <p className="p-p mt16">
              {t('aboutus:section.history.description.3')}
            </p>
            <p className="p-p mt16">
              {t('aboutus:section.history.description.4')}
            </p>
          </div>
          <div className={styles['section-history-image']}>
            <div className={styles['section-history-image-wrapper']}>
              <Image
                src={images.history[imgLocaleKey]}
                alt={t('aboutus:section.history.image.alt')}
                style={{
                  height: 'auto',
                  width: '100%',
                }}
                width={828}
                height={659}
                sizes="(max-width: 720px) 100vw, 40vw"
              />
            </div>
          </div>
        </div>
        <div className="p-body mt72">
          <header>
            <h2 className="p-h2 p--serif">
              {t('aboutus:section.numbers.milestones', 'Milestones')}
            </h2>
          </header>
          <div
            data-aos="animate-underline"
            data-aos-once="true"
            className={styles.separator}
          />
          <div
            className={`mt32 pb32 d-flex f-wrap jc-between ${styles['section-numbers-container']}`}
          >
            <div
              data-aos="fade-left"
              data-aos-duration="900"
              data-aos-once="true"
              className={`wmx3 mr8 ${styles['section-numbers-item']}`}
            >
              <h4 className="p-h1 p--serif tc-primary-500">+90,000</h4>
              <p className="mt8 tc-grey-900">
                {t('aboutus:section.numbers.customers')}
              </p>
            </div>
            <div
              data-aos="fade-left"
              data-aos-duration="900"
              data-aos-delay="300"
              data-aos-once="true"
              className={`wmx3 mr8 ${styles['section-numbers-item']}`}
            >
              <h4 className="p-h1 p--serif tc-primary-500">+15</h4>
              <p className="mt8 tc-grey-900">
                {t('aboutus:section.numbers.products')}
              </p>
            </div>
            <div
              data-aos="fade-left"
              data-aos-duration="900"
              data-aos-delay="600"
              data-aos-once="true"
              className={`wmx3 mr8 ${styles['section-numbers-item']}`}
            >
              <h4 className="p-h1 p--serif tc-primary-500">+20,000</h4>
              <p className="mt8 tc-grey-900">
                {t('aboutus:section.numbers.claims')}
              </p>
            </div>
            <div
              data-aos="fade-left"
              data-aos-duration="900"
              data-aos-delay="900"
              data-aos-once="true"
              className={`wmx3 mr8 ${styles['section-numbers-item']}`}
            >
              <h4 className="p-h1 p--serif tc-primary-500">+4.9</h4>
              <p className="mt8 tc-grey-900">
                {t('aboutus:section.numbers.reviews')}
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className={styles['section-us']}>
        <div className="p-body">
          <header>
            <h2 className="p-h1 p--serif">{t('aboutus:section.who.title')}</h2>
            <div
              data-aos="animate-underline"
              data-aos-once="true"
              className={`ml-auto mr-auto ${styles.separator}`}
            />
            <p className="p-p wmx6 mt8">{t('aboutus:section.who.subtitle')}</p>
            <p className="p-p wmx6 mt8">
              {t('aboutus:section.who.action.1', 'Want to join us?')}{' '}
              <a
                href={`${subdomain === DEFAULT_SUBDOMAIN ? '' : website.base}${
                  nonProductLinks.CAREER.path
                }#positions`}
                className="p-a fw-bold"
              >
                {t(
                  'aboutus:section.who.action.2',
                  'Check out our open positions'
                )}
              </a>
            </p>
          </header>
          <div className="mt32">
            <video
              className={styles.teamVideo}
              loop
              muted
              autoPlay
              playsInline
              poster="static/about-us/team-section-placeholder.png"
            >
              <source
                src="/static/about-us/team-section.webm"
                type="video/webm"
              />
              <source
                src="/static/about-us/team-section.mp4"
                type="video/mp4"
              />
              Sorry, your browser doesn&apos;t support embedded videos.
            </video>
          </div>
        </div>
      </section>
      <section className="pt96">
        <div className="p-body">
          <header>
            <h2 className="p-h2 pt96 p--serif ta-center">
              {t('aboutus:section.blog.title', 'News from Feather')}
            </h2>
          </header>
          <div
            data-aos="animate-underline"
            data-aos-once="true"
            className={`ml-auto mr-auto ${styles.separator}`}
          />
          <div
            className={`mt32 pb32 d-flex f-wrap jc-between ${styles['section-news-container']}`}
          >
            {news(t).map((item) => (
              <a
                href={item.link}
                key={item.title}
                target="_blank"
                rel="noreferrer"
              >
                <ImageCard
                  className="wmx4"
                  title={item.title}
                  description={item.description}
                  img={{
                    src: item.image,
                    alt: item.imageAlt,
                  }}
                  width={288}
                  height={124}
                />
              </a>
            ))}
          </div>
          <div className="ta-center mb72">
            <a className="p-btn p-btn--primary" href={blog.base}>
              {t('aboutus:section.blog.action', 'Read more')}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(
      i18nLocaleFormatter(locale) || FORMATTED_DEFAULT_LOCALE,
      ['common', 'aboutus']
    )),
  },
});

export default AboutUs;
